import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import CustomTable from "../../components/common/CustomTable";
import CustomSearch from "../../components/common/CustomSearch";
import CustomPaging from "../../components/common/CustomPaging";
import CustomButton from "../../components/common/CustomButton";
import AddDiscountModal from "./AddDiscountModal";
import UpdateDiscountModal from "./UpdateDiscountModal";
import config from "../../config";
import axios from "axios";
import useLayoutStore from "../../store/useLayoutStore";

const drawerWidth = 240;
const collapsedWidth = 80;

const Discounts = () => {
  const { isSidebarOpen } = useLayoutStore();
  const [discounts, setDiscounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingDiscountId, setEditingDiscountId] = useState(null);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/discounts`);
      setDiscounts(res.data);
    } catch (err) {
      setError("Lỗi khi tải danh sách khuyến mãi");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa khuyến mãi này?")) {
      try {
        await axios.delete(`${config.apiBaseUrl}/discounts/${id}`);
        fetchDiscounts();
      } catch {
        setError("Lỗi khi xóa khuyến mãi");
      }
    }
  };

  const handleEdit = (discountId) => {
    setEditingDiscountId(discountId);
    setOpenEditModal(true);
  };

  // Filter & Paginate
  const filtered = discounts.filter((d) =>
    `${d.productId || ""}-${d.discountValue || ""}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );
  const startIndex = (page - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  // Table Columns
  const columns = [
    { field: "discountId", label: "ID", minWidth: "80px" },
    { field: "productId", label: "Product ID", minWidth: "100px" },
    { field: "categoryId", label: "Category ID", minWidth: "100px" },
    {
      field: "discountValue",
      label: "Discount",
      minWidth: "120px",
      customRender: (row) => {
        const type = row.discountType || row.DiscountType; // check cả camelCase và PascalCase
        if (type === "percent") {
          return `${row.discountValue}%`;
        }
        return `${row.discountValue.toLocaleString("vi-VN")} đ`;
      },
    },
    { field: "discountType", label: "Type", minWidth: "80px" },
    { field: "startDate", label: "Start", minWidth: "180px" },
    { field: "endDate", label: "End", minWidth: "180px" },
    {
      field: "actions",
      label: "Actions",
      minWidth: "150px",
      customRender: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <IconButton
            color="secondary"
            onClick={() => handleEdit(row.discountId)}
          >
            <Edit />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDelete(row.discountId)}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 1.5, md: 2 },
        width: "100%",
        minWidth: 0, // QUAN TRỌNG
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflowX: "hidden", // chặn tràn ngang
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Discount Management
        </Typography>
        <CustomButton
          text="+ Add Discount"
          onClick={() => setOpenAddModal(true)}
          customColor="#1976d2"
        />
      </Box>

      {/* Search */}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}
      >
        <CustomSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </Box>

      {/* Table */}
      {loading ? (
        <CircularProgress sx={{ alignSelf: "center", mt: 5 }} />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <CustomTable columns={columns} data={paginated} />
          <CustomPaging
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            totalItems={filtered.length}
          />
        </>
      )}

      {/* Modals */}
      <AddDiscountModal
        open={openAddModal}
        onClose={() => {
          setOpenAddModal(false);
          fetchDiscounts();
        }}
      />

      {openEditModal && editingDiscountId && (
        <UpdateDiscountModal
          open={openEditModal}
          onClose={() => {
            setOpenEditModal(false);
            setEditingDiscountId(null);
            fetchDiscounts();
          }}
          discountId={editingDiscountId}
        />
      )}
    </Box>
  );
};

export default Discounts;
